/**
 * Централизованная конфигурация URL для разных environment
 * Поддерживает development (порты 3000/8000) и production (nginx прокси)
 */

export interface EnvironmentConfig {
  apiBaseUrl: string;
  mediaBaseUrl: string;
  wsBaseUrl: string;
  isProduction: boolean;
  isDevelopment: boolean;
}

/**
 * Определяет текущее окружение на основе hostname и переменных
 */
export const detectEnvironment = (): 'development' | 'production' => {
  // В production за nginx прокси порты будут скрыты
  const hostname = window.location.hostname;
  const port = window.location.port;

  // Production признаки: нет портов 3000/8000, не localhost
  const isProduction = !port || (port !== '3000' && port !== '8000' && port !== '5173');
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

  console.log('🔍 Environment detection:', { hostname, port, isProduction, isLocalhost });

  // Если есть переменная окружения принудительно указывающая на production
  if (import.meta.env.VITE_ENVIRONMENT === 'production') {
    console.log('✅ Forced production mode by VITE_ENVIRONMENT');
    return 'production';
  }

  // Если есть переменная окружения принудительно указывающая на development
  if (import.meta.env.VITE_ENVIRONMENT === 'development') {
    console.log('✅ Forced development mode by VITE_ENVIRONMENT');
    return 'development';
  }

  // Автоопределение: если порт указан и это localhost - development
  if (isLocalhost && port) {
    console.log('🏠 Development mode detected (localhost with port)');
    return 'development';
  }

  // Иначе считаем production (реальный домен или localhost без порта)
  console.log('🌐 Production mode detected');
  return 'production';
};

/**
 * Получает базовый URL для API запросов
 */
export const getApiBaseUrl = (): string => {
  const environment = detectEnvironment();

  if (environment === 'production') {
    // В production используем относительные URL, чтобы не смешивать http/https
    console.log('🏭 Production API URL: пустая строка (current origin)');
    return '';
  }

  // Development mode - используем Vite proxy для удобства
  // Vite проксирует /api на backend:8000 внутри Docker сети
  console.log('🏠 Development API URL: пустая строка (Vite proxy)');
  return ''; // Пустая строка означает относительные URL, которые будут проксированы Vite
};

/**
 * Получает базовый URL для медиафайлов
 */
export const getMediaBaseUrl = (): string => {
  const environment = detectEnvironment();

  if (environment === 'production') {
    // В production используем относительные URL, чтобы браузер сохранял текущий protocol/origin
    console.log('🏭 Production Media URL: пустая строка (current origin)');
    return '';
  }

  // В development режиме используем Vite proxy
  // Нужно добавить проксирование /media в vite.config.ts
  console.log('🎥 Development Media URL: пустая строка (Vite proxy)');
  return ''; // Также используем относительные URL через Vite proxy
};

/**
 * Получает базовый URL для WebSocket соединений
 */
export const getWebSocketBaseUrl = (): string => {
  const environment = detectEnvironment();

  if (environment === 'production') {
    // В production nginx проксирует WebSocket на /ws
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const baseUrl = `${protocol}//${host}`;
    console.log('🏭 Production WebSocket URL:', baseUrl);
    return baseUrl;
  }

  // В development WebSocket нужно подключаться напрямую к backend
  // так как Vite не может проксировать WebSocket на /ws
  const hostname = window.location.hostname;
  const wsUrl = `ws://${hostname}:8000`;
  console.log('💬 Development WebSocket URL:', wsUrl);
  return wsUrl;
};

/**
 * Преобразует URL медиафайла, который может прийти с сервера
 * На случай если сервер возвращает URLs с захардкоженным localhost
 */
export const convertMediaUrl = (serverUrl: string): string => {
  if (!serverUrl) return serverUrl;

  const environment = detectEnvironment();

  // В production URLs должны быть относительными или уже правильными
  if (environment === 'production') {
    // Если URL абсолютный с любым хостом - конвертируем в относительный
    if (serverUrl.match(/^https?:\/\/[^\/]+/)) {
      const relativePath = serverUrl.replace(/^https?:\/\/[^\/]+/, '');
      console.log('🔄 Converting absolute URL to relative:', serverUrl, '→', relativePath);
      return relativePath;
    }
    return serverUrl;
  }

  // В development режиме всё должно быть относительными путями для Vite proxy
  // Если URL абсолютный - конвертируем в относительный
  if (serverUrl.match(/^https?:\/\/[^\/]+/)) {
    const relativePath = serverUrl.replace(/^https?:\/\/[^\/]+/, '');
    console.log('🔄 Converting absolute URL to relative for Vite proxy:', serverUrl, '→', relativePath);
    return relativePath;
  }

  // Если URL уже относительный - возвращаем как есть
  console.log('✅ URL already relative:', serverUrl);
  return serverUrl;
};

/**
 * Получает полный URL для API endpoint
 */
export const getApiUrl = (endpoint: string): string => {
  const baseUrl = getApiBaseUrl();
  const environment = detectEnvironment();

  if (environment === 'production') {
    // В production nginx проксирует /api на backend
    return `${baseUrl}/api/v1${endpoint}`;
  }

  // В development Vite проксирует /api на backend:8000
  return `/api/v1${endpoint}`;
};

/**
 * Получает полный URL для WebSocket endpoint
 */
export const getWebSocketUrl = (endpoint: string): string => {
  const baseUrl = getWebSocketBaseUrl();
  const environment = detectEnvironment();

  if (environment === 'production') {
    // В production nginx проксирует /ws на backend WebSocket
    return `${baseUrl}/ws/v1${endpoint}`;
  }

  // В development WebSocket подключается напрямую к backend:8000
  return `${baseUrl}/api/v1/ws${endpoint}`;
};

/**
 * Создает объект конфигурации для текущего окружения
 */
export const createEnvironmentConfig = (): EnvironmentConfig => {
  const environment = detectEnvironment();
  const isProduction = environment === 'production';

  const config: EnvironmentConfig = {
    apiBaseUrl: getApiBaseUrl(),
    mediaBaseUrl: getMediaBaseUrl(),
    wsBaseUrl: getWebSocketBaseUrl(),
    isProduction,
    isDevelopment: !isProduction
  };

  console.log('⚙️ Environment config created:', config);
  return config;
};

// Экспортируем готовую конфигурацию
export const environmentConfig = createEnvironmentConfig();
